import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { buildApiUrl } from '../../config/apiConfig';
import './ExamPage.css';
import { log, logError } from '../../utils/logger';

const ExamPage = () => {
  const { courseId, examId } = useParams();
  const { user, isAuthenticated, token } = useAuth();
  const { notify } = useNotifications();
  const navigate = useNavigate();

  const [exam, setExam] = useState(null);
  const [userAnswers, setUserAnswers] = useState({});
  const [userAnswersMap, setUserAnswersMap] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      notify({
        message: 'Debes iniciar sesión para realizar el examen.',
        type: 'info',
      });
      navigate('/login', { state: { from: `/my-courses/${courseId}/exam/${examId}` } });
      return;
    }

    fetchExam();
  }, [examId, isAuthenticated, token, navigate, notify, courseId]);

  const fetchExam = async () => {
    setLoading(true);
    try {
      // Primero intentar obtener resultados si ya completó el examen
      const resultsResponse = await fetch(buildApiUrl(`/api/exams/${examId}/results`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (resultsResponse.ok) {
        const resultsData = await resultsResponse.json();
        setResult(resultsData.result);
        setShowResults(true);
        // Cargar el examen con corrección
        fetchExamWithCorrection(resultsData);
        return;
      } else if (resultsResponse.status === 404) {
        // No hay resultados aún, continuar para cargar el examen normal
        log('[ExamPage] No hay resultados previos, cargando examen para resolver');
      }

      // Si no hay resultados, obtener el examen para resolver
      const examResponse = await fetch(buildApiUrl(`/api/exams/${examId}/take`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!examResponse.ok) {
        const errorData = await examResponse.json().catch(() => ({ message: 'Error desconocido' }));
        throw new Error(errorData.error || 'Error al cargar el examen');
      }

      const examData = await examResponse.json();
      setExam(examData.exam);

      // Cargar respuestas guardadas si existen
      if (examData.userAnswers && examData.userAnswers.length > 0) {
        const answersMap = new Map();
        examData.userAnswers.forEach((answer) => {
          answersMap.set(answer.preguntaId, answer.respuestaId);
        });
        setUserAnswersMap(answersMap);
        const answersObj = {};
        examData.userAnswers.forEach((answer) => {
          answersObj[answer.preguntaId] = answer.respuestaId;
        });
        setUserAnswers(answersObj);
      }
    } catch (err) {
      logError('[ExamPage] Error fetching exam:', err);
      notify({
        message: err.message || 'Error al cargar el examen.',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchExamWithCorrection = async (resultsData) => {
    try {
      const examResponse = await fetch(buildApiUrl(`/api/exams/${examId}/take`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (examResponse.ok) {
        const examData = await examResponse.json();
        let examWithCorrection = examData.exam;
        
        // Si tenemos datos de corrección, actualizar las respuestas con esCorrecta
        if (resultsData.correction && examWithCorrection.preguntas) {
          examWithCorrection.preguntas = examWithCorrection.preguntas.map((pregunta) => {
            const correctionData = resultsData.correction.find(c => c.preguntaId === pregunta.id);
            if (correctionData && correctionData.todasLasRespuestas) {
              // Actualizar las respuestas con la información de corrección
              pregunta.respuestas = pregunta.respuestas.map((respuesta) => {
                const correctedAnswer = correctionData.todasLasRespuestas.find(r => r.id === respuesta.id);
                if (correctedAnswer) {
                  return {
                    ...respuesta,
                    esCorrecta: correctedAnswer.esCorrecta
                  };
                }
                return respuesta;
              });
            }
            return pregunta;
          });
        }
        
        setExam(examWithCorrection);
        
        // Cargar respuestas del usuario desde la corrección
        if (resultsData.correction) {
          const answersMap = new Map();
          resultsData.correction.forEach((item) => {
            if (item.respuestaUsuario) {
              answersMap.set(item.preguntaId, item.respuestaUsuario.respuestaId);
            }
          });
          setUserAnswersMap(answersMap);
        }
      }
    } catch (err) {
      logError('[ExamPage] Error fetching exam with correction:', err);
    }
  };

  const handleAnswerSelect = async (preguntaId, respuestaId) => {
    // Actualizar estado local inmediatamente
    setUserAnswers((prev) => ({
      ...prev,
      [preguntaId]: respuestaId,
    }));

    const newMap = new Map(userAnswersMap);
    newMap.set(preguntaId, respuestaId);
    setUserAnswersMap(newMap);

    // Guardar respuesta en el servidor
    try {
      await fetch(buildApiUrl(`/api/exams/${examId}/answer`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          preguntaId: parseInt(preguntaId),
          respuestaId: parseInt(respuestaId),
        }),
      });
    } catch (err) {
      logError('[ExamPage] Error saving answer:', err);
      notify({
        message: 'Error al guardar la respuesta. Intenta nuevamente.',
        type: 'warning',
      });
    }
  };

  const handleSubmitExam = async () => {
    if (!exam || !exam.preguntas) return;

    const totalQuestions = exam.preguntas.length;
    const answeredQuestions = Object.keys(userAnswers).length;

    if (answeredQuestions < totalQuestions) {
      const confirm = window.confirm(
        `Has respondido ${answeredQuestions} de ${totalQuestions} preguntas. ¿Deseas enviar el examen de todas formas?`
      );
      if (!confirm) return;
    }

    setSubmitting(true);
    try {
      const submitResponse = await fetch(buildApiUrl(`/api/exams/${examId}/submit`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}), // Enviar body vacío para cumplir con Content-Type
      });

      if (!submitResponse.ok) {
        const errorData = await submitResponse.json().catch(() => ({ message: 'Error desconocido' }));
        throw new Error(errorData.error || 'Error al enviar el examen');
      }

      const submitData = await submitResponse.json();
      setResult(submitData.result);
      setShowResults(true);

      // Obtener corrección completa
      const resultsResponse = await fetch(buildApiUrl(`/api/exams/${examId}/results`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (resultsResponse.ok) {
        const resultsData = await resultsResponse.json();
        fetchExamWithCorrection(resultsData);
      }

      const puntajeNum = parseFloat(submitData.details.puntaje) || 0;
      notify({
        message: submitData.result.aprobado
          ? `¡Felicitaciones! Aprobaste el examen con ${puntajeNum.toFixed(0)}%`
          : `Examen completado. Obtuviste ${puntajeNum.toFixed(0)}% (mínimo requerido: ${exam.notaMinima}%)`,
        type: submitData.result.aprobado ? 'success' : 'info',
      });
    } catch (err) {
      logError('[ExamPage] Error submitting exam:', err);
      notify({
        message: err.message || 'Error al enviar el examen.',
        type: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="exam-page loading-state">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando examen...</span>
        </div>
        <p>Cargando examen...</p>
      </div>
    );
  }

  if (!exam) {
    return (
      <div className="exam-page error-state">
        <div className="alert alert-danger" role="alert">
          <i className="fas fa-exclamation-circle me-2"></i>
          Examen no encontrado
        </div>
        <button className="btn btn-primary mt-3" onClick={() => navigate(`/my-courses/${courseId}/content`)}>
          <i className="fas fa-arrow-left me-2"></i>
          Volver al Curso
        </button>
      </div>
    );
  }

  const totalQuestions = exam.preguntas?.length || 0;
  const answeredQuestions = Object.keys(userAnswers).length;
  const isComplete = answeredQuestions === totalQuestions;

  return (
    <div className="exam-page">
      <div className="exam-container">
        {/* Header */}
        <div className="exam-header">
          <button className="btn btn-link back-button" onClick={() => navigate(`/my-courses/${courseId}/content`)}>
            <i className="fas fa-arrow-left me-2"></i>
            Volver al Curso
          </button>
          <div className="exam-header-content">
            <h1 className="exam-title">{exam.titulo}</h1>
            {exam.descripcion && <p className="exam-description">{exam.descripcion}</p>}
            <div className="exam-info">
              <span>
                <i className="fas fa-question-circle me-1"></i>
                {totalQuestions} {totalQuestions === 1 ? 'pregunta' : 'preguntas'}
              </span>
              <span>
                <i className="fas fa-check-circle me-1"></i>
                Nota mínima: {exam.notaMinima}%
              </span>
              {!showResults && (
                <span className={isComplete ? 'text-success' : 'text-warning'}>
                  <i className="fas fa-edit me-1"></i>
                  {answeredQuestions} / {totalQuestions} respondidas
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Results Summary */}
        {showResults && result && (
          <div className={`exam-result-summary ${result.aprobado ? 'approved' : 'failed'}`}>
            <div className="result-icon">
              <i className={`fas fa-${result.aprobado ? 'check' : 'times'}-circle`}></i>
            </div>
            <div className="result-content">
              <h2>{result.aprobado ? '¡Felicitaciones! Aprobaste el examen' : 'Examen no aprobado'}</h2>
              <div className="result-score">
                <span className="score-value">{(parseFloat(result.puntaje) || 0).toFixed(0)}%</span>
                <span className="score-label">Puntaje obtenido</span>
              </div>
              <p className="result-message">
                {result.aprobado
                  ? 'Has completado exitosamente el examen del curso.'
                  : `Necesitas al menos ${exam.notaMinima}% para aprobar. Puedes intentar nuevamente.`}
              </p>
            </div>
          </div>
        )}

        {/* Questions */}
        {exam.preguntas && exam.preguntas.length > 0 ? (
          <div className="exam-questions">
            {exam.preguntas.map((pregunta, index) => {
              const selectedAnswerId = userAnswersMap.get(pregunta.id);
              const isMultiple = pregunta.tipo === 'multiple';

              return (
                <div key={pregunta.id} className="exam-question-card">
                  <div className="question-header">
                    <span className="question-number">Pregunta {index + 1}</span>
                    <span className="question-type">{isMultiple ? 'Múltiple opción' : 'Opción única'}</span>
                  </div>
                  <h3 className="question-text">{pregunta.pregunta}</h3>
                  <div className="question-answers">
                    {pregunta.respuestas && pregunta.respuestas.length > 0 ? (
                      pregunta.respuestas.map((respuesta) => {
                        const isSelected = selectedAnswerId === respuesta.id;
                        const isCorrect = showResults && respuesta.esCorrecta;
                        const wasSelected = showResults && isSelected;
                        const showCorrect = showResults && isCorrect;
                        const showIncorrect = showResults && wasSelected && !isCorrect;

                        return (
                          <label
                            key={respuesta.id}
                            className={`answer-option ${isSelected ? 'selected' : ''} ${
                              showCorrect ? 'correct' : ''
                            } ${showIncorrect ? 'incorrect' : ''} ${showResults ? 'disabled' : ''}`}
                          >
                            <input
                              type={isMultiple ? 'checkbox' : 'radio'}
                              name={`pregunta-${pregunta.id}`}
                              value={respuesta.id}
                              checked={isSelected}
                              onChange={() => !showResults && handleAnswerSelect(pregunta.id, respuesta.id)}
                              disabled={showResults}
                            />
                            <span className="answer-text">{respuesta.texto}</span>
                            {showResults && (
                              <span className="answer-feedback">
                                {showCorrect && <i className="fas fa-check-circle text-success"></i>}
                                {showIncorrect && <i className="fas fa-times-circle text-danger"></i>}
                              </span>
                            )}
                          </label>
                        );
                      })
                    ) : (
                      <p className="text-muted">Esta pregunta no tiene respuestas disponibles.</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="exam-empty">
            <i className="fas fa-inbox"></i>
            <p>Este examen aún no tiene preguntas disponibles.</p>
          </div>
        )}

        {/* Submit Button */}
        {!showResults && (
          <div className="exam-actions">
            <button
              className="btn btn-primary btn-lg submit-exam-button"
              onClick={handleSubmitExam}
              disabled={submitting || totalQuestions === 0}
            >
              {submitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Enviando...
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane me-2"></i>
                  Enviar Examen
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExamPage;

